/**
 * RentSplitEditor — Batch #14.226
 *
 * Composant partagé entre le portail (proprio) et l'admin pour éditer la
 * répartition du loyer (`rent_split_by_tenant`) entre les co-locataires d'un
 * bail.
 *
 * Props :
 *   - lease           : { id, rent_monthly, tenant_ids, rent_split_by_tenant?, rent_split_mode? }
 *   - tenants         : [{ id, first_name, last_name, ... }]
 *   - apiUrl          : URL PATCH absolue (admin OU portail)
 *   - authHeaders     : () => { Authorization: 'Bearer …' }
 *   - onSaved?        : (updated: { rent_split_by_tenant, rent_split_mode, rent_monthly }) => void
 *   - testidPrefix    : préfixe pour les data-testid (ex: 'portal-rsplit')
 *
 * Visible UNIQUEMENT si le bail a 2 locataires ou plus.
 */
import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "@/lib/toast";
import { Loader2, RotateCcw, Save, Users, AlertCircle } from "lucide-react";

const fmtMoney = (n) =>
  (typeof n === "number" ? n : Number(n) || 0).toLocaleString("fr-CA", {
    style: "currency", currency: "CAD", minimumFractionDigits: 2,
  });

export function RentSplitEditor({
  lease, tenants, apiUrl, authHeaders, onSaved, testidPrefix = "rent-split",
}) {
  const rentMonthly = Number(lease?.rent_monthly || 0);
  const tenantIds = (lease?.tenant_ids || []);

  // tenants disponibles dans l'ordre de tenant_ids
  const orderedTenants = useMemo(() => {
    const byId = Object.fromEntries((tenants || []).map((t) => [t.id, t]));
    return tenantIds.map((tid) => byId[tid]).filter(Boolean);
  }, [tenantIds, tenants]);

  const savedSplit = lease?.rent_split_by_tenant || {};
  const isCustom = (lease?.rent_split_mode === "custom") && Object.keys(savedSplit).length > 0;
  const defaultEqual = orderedTenants.length ? rentMonthly / orderedTenants.length : 0;

  const [splits, setSplits] = useState(() => {
    const s = {};
    for (const t of orderedTenants) {
      s[t.id] = isCustom ? Number(savedSplit[t.id] || 0) : Number(defaultEqual.toFixed(2));
    }
    return s;
  });
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Re-sync quand le lease change — utilise un track-id local au lieu d'un useEffect
  // (cf. https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes)
  const [trackKey, setTrackKey] = useState(`${lease?.id}|${lease?.rent_split_mode}`);
  const currentKey = `${lease?.id}|${lease?.rent_split_mode}`;
  if (trackKey !== currentKey) {
    setTrackKey(currentKey);
    const next = {};
    for (const t of orderedTenants) {
      next[t.id] = isCustom ? Number(savedSplit[t.id] || 0) : Number(defaultEqual.toFixed(2));
    }
    setSplits(next);
  }

  // Hidden si solo — APRÈS tous les hooks pour respecter les règles React
  if (orderedTenants.length < 2 || !rentMonthly) return null;

  const total = Object.values(splits).reduce((s, v) => s + (Number(v) || 0), 0);
  const diff = rentMonthly - total;
  const sumOk = Math.abs(diff) < 0.01;

  const setOne = (tid, value) => {
    setSplits((s) => ({ ...s, [tid]: value === "" ? "" : Number(value) }));
  };

  const resetEqual = () => {
    const eq = Number(defaultEqual.toFixed(2));
    // Mettre les n-1 premiers à eq et le dernier au reste pour arrondi parfait
    const next = {};
    let assigned = 0;
    orderedTenants.forEach((t, i) => {
      if (i < orderedTenants.length - 1) {
        next[t.id] = eq;
        assigned += eq;
      } else {
        next[t.id] = Number((rentMonthly - assigned).toFixed(2));
      }
    });
    setSplits(next);
  };

  const save = async () => {
    if (!sumOk) {
      toast.error(`Somme incorrecte : ${fmtMoney(total)} / ${fmtMoney(rentMonthly)}`);
      return;
    }
    setSaving(true);
    try {
      // Si répartition strictement égale (diff < 1 cent), on envoie {} pour revenir au mode 'equal'
      const eq = Number(defaultEqual.toFixed(2));
      const isStrictlyEqual = orderedTenants.every((t) =>
        Math.abs(Number(splits[t.id] || 0) - eq) < 0.01,
      );
      const payload = isStrictlyEqual
        ? { rent_split_by_tenant: {} }
        : { rent_split_by_tenant: splits };
      const resp = await axios.patch(apiUrl, payload, { headers: authHeaders() });
      toast.success(
        isStrictlyEqual
          ? "Répartition remise en mode égal (auto)"
          : "Répartition personnalisée enregistrée",
      );
      onSaved?.(resp.data);
      if (isStrictlyEqual) setExpanded(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="rounded-lg border border-amber-200 bg-amber-50/40 p-2.5 space-y-2"
      data-testid={`${testidPrefix}-card`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between text-left"
        data-testid={`${testidPrefix}-toggle`}
      >
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-800">
          <Users className="w-3.5 h-3.5" />
          Répartition du loyer ({orderedTenants.length} loc.)
          <span className={`ml-1 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${
            isCustom ? "bg-amber-200 text-amber-900" : "bg-emerald-100 text-emerald-700"
          }`}>
            {isCustom ? "Personnalisée" : "Égale (auto)"}
          </span>
        </span>
        <span className="text-[10px] text-slate-500">{expanded ? "Masquer" : "Éditer"}</span>
      </button>

      {expanded && (
        <>
          <div className="space-y-1.5">
            {orderedTenants.map((t) => {
              const tName = `${t.first_name || ""} ${t.last_name || ""}`.trim() || `Locataire ${t.id.slice(0, 6)}`;
              return (
                <div
                  key={t.id}
                  className="flex items-center gap-2"
                  data-testid={`${testidPrefix}-row-${t.id}`}
                >
                  <span className="flex-1 text-[11px] text-slate-700 truncate" title={tName}>
                    {tName}
                  </span>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={splits[t.id] ?? ""}
                      onChange={(e) => setOne(t.id, e.target.value)}
                      className="w-28 px-2 py-1 text-xs border border-slate-300 rounded bg-white text-right font-mono pr-6 focus:outline-none focus:border-amber-500"
                      data-testid={`${testidPrefix}-input-${t.id}`}
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">$</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div
            className={`text-[11px] font-mono flex items-center gap-1 ${
              sumOk ? "text-emerald-700" : "text-rose-700"
            }`}
            data-testid={`${testidPrefix}-total`}
          >
            {!sumOk && <AlertCircle className="w-3 h-3" />}
            Total : {fmtMoney(total)} / {fmtMoney(rentMonthly)}
            {!sumOk && (
              <span className="ml-1">
                {diff > 0 ? `(reste ${fmtMoney(diff)})` : `(excès ${fmtMoney(-diff)})`}
              </span>
            )}
          </div>

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={resetEqual}
              disabled={saving}
              className="text-[10px] px-2 py-1 rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-40"
              data-testid={`${testidPrefix}-reset`}
              title="Remettre la répartition égale"
            >
              <RotateCcw className="w-3 h-3" /> Égal
            </button>
            <div className="flex-1" />
            <button
              type="button"
              onClick={save}
              disabled={saving || !sumOk}
              className="text-[10px] px-2 py-1 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40 inline-flex items-center gap-1 font-semibold"
              data-testid={`${testidPrefix}-save`}
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              Enregistrer
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default RentSplitEditor;
